import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Box, Grid, CircularProgress, Alert } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import { deleteReferralRecordByPatientId } from '../../features/lma/referralsSlice';
import { fetchDropdownData } from '../../features/lma/dropdownSlice';
import { LeadManagementProps } from './LeadManagement.types';
import CustomTable from 'components/LMA/Table';
import { StyledContainer, StyledPaper } from 'components/LMA/LMA.style';
import AlertMessage from '../LMA/AlertMessage';
import PaginationControls from 'components/LMA/PaginationControls';
import { ActiveFilters, ActiveSorts } from 'components/LMA/ActiveFilters';
import MergeComparisonModal from 'components/LMA/MergeComparisonModal';
import DynamicFilterPanel from 'components/LMA/DynamicFilterPanel';
import { DeduplicationMatch } from 'components/LMA/Table/DeduplicationTable';
import { ReferralData } from 'components/LMA/types';
import { useAppData, useReferralData, useMergeModal } from 'components/LMA/hooks';
import Header from 'components/LMA/Header/Header';
import { useCurrentTab, useLoadAllTabs } from 'hooks/useCurrentTab';
import { getTabConfigurationByIndex } from './utils/configBuilder';
import { buildPageConfig, getExpandedViewConfig } from './utils/configAdapter';
import { FilterButton, TabBar } from 'components/LMA';
import { TAB_KEYS, normalizeTabKey } from './constants';
import { addLMAUser } from 'src/services/leadManagementApi';
import { logger } from '../../logger';
import ToastNotification from '../shared/ToastNotification/ToastNotification';
import ConfirmationDialog from 'components/LMA/Table/ConfirmationDialog';

const CURRENT_USER = 'Current User';
const MAX_LOCKED_RECORDS = 5;

const LeadManagement = (props: LeadManagementProps): JSX.Element => {
  const baseTheme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const [tabValue, setTabValue] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchDropdownData());
  }, [dispatch]);

  // Add user to LMAUser table on component mount using username from localStorage (set during login)
  const username = localStorage.getItem('lma-username');
  useEffect(() => {
    if (!username) return;
    const addUser = async () => {
      try {
        logger.info(`Adding user to LMAUsers table`, {
          response: username,
          payload: {},
        });
        const userId = await addLMAUser(username);
        if (userId) {
          localStorage.setItem('lma-userId', userId);
        }
      } catch (error: any) {
        logger.error(`An error occurred while adding the user`, {
          error: error?.message,
          payload: {},
        });
        dispatch({
          type: 'toast/showToast',
          payload: {
            message: error?.response?.data?.message || 'Failed to add user to LMA',
            severity: 'error',
          },
        });
      }
    };
    addUser();
  }, [username, dispatch]);
  // Build dynamic tabs from Sitecore props
  const dynamicTabs = useMemo(() => {
    const tabs = [];

    if (props.fields.Referral) {
      const sitecoreTabKey = props.fields.Referral.fields.TabKey?.value || '';
      tabs.push({
        label: props.fields.Referral.fields.TabName?.value || 'Referrals',
        value: 0,
        dataKey: normalizeTabKey(sitecoreTabKey) || TAB_KEYS.REFERRALS,
      });
    }

    if (props.fields.NonReferral) {
      const sitecoreTabKey = props.fields.NonReferral.fields.TabKey?.value || '';
      tabs.push({
        label: props.fields.NonReferral.fields.TabName?.value || 'Non-Referrals',
        value: 1,
        dataKey: normalizeTabKey(sitecoreTabKey) || TAB_KEYS.NON_REFERRALS,
      });
    }

    if (props.fields.ProcessedToCC) {
      const sitecoreTabKey = props.fields.ProcessedToCC.fields.TabKey?.value || '';
      tabs.push({
        label: props.fields.ProcessedToCC.fields.TabName?.value || 'Processed to CC',
        value: 2,
        dataKey: normalizeTabKey(sitecoreTabKey) || TAB_KEYS.PROCESSED_REFERRALS,
      });
    }

    return tabs;
  }, [props.fields]);

  const currentTabKey = useMemo(() => {
    const currentTab = dynamicTabs.find((tab) => tab.value === tabValue);
    return currentTab?.dataKey || '';
  }, [tabValue, dynamicTabs]);

  const {
    data,
    totalRecords,
    loading,
    error,
    currentPage,
    pageSize,
    filters,
    sortBy,
    sortOrder,
    applyFilters,
    changePage,
    changePageSize,
    changeSorting,
    clearSorting,
    loadData,
  } = useCurrentTab({
    currentTabKey,
    referralStatusFilters: props.fields.Referral?.fields?.LeadStatusFilters,
    nonReferralStatusFilters: props.fields.NonReferral?.fields?.LeadStatusFilters,
    processedStatusFilters: props.fields.ProcessedToCC?.fields?.LeadStatusFilters,
  });

  // Get total records from all tabs for tab counts
  const referralsTotal = useSelector((state: RootState) => state.lmaReferrals.totalRecords);
  const nonReferralsTotal = useSelector((state: RootState) => state.lmaNonReferrals.totalRecords);
  const processedTotal = useSelector(
    (state: RootState) => state.lmaProcessedReferrals.totalRecords
  );

  const allTabTotals = useMemo(
    () => ({
      [TAB_KEYS.REFERRALS]: referralsTotal,
      [TAB_KEYS.NON_REFERRALS]: nonReferralsTotal,
      [TAB_KEYS.PROCESSED_REFERRALS]: processedTotal,
    }),
    [referralsTotal, nonReferralsTotal, processedTotal]
  );

  const { loadAllTabs } = useLoadAllTabs({
    referralStatusFilters: props.fields.Referral?.fields?.LeadStatusFilters,
    nonReferralStatusFilters: props.fields.NonReferral?.fields?.LeadStatusFilters,
    processedStatusFilters: props.fields.ProcessedToCC?.fields?.LeadStatusFilters,
  });

  useEffect(() => {
    loadAllTabs();
  }, []);

  useEffect(() => {
    loadData();
  }, [currentTabKey]);

  // Custom theme with specific color
  const customTheme = useMemo(
    () =>
      createTheme({
        ...baseTheme,
        palette: {
          ...baseTheme.palette,
          themeColor: {
            main: '#255E5E',
          },
        },
      }),
    [baseTheme]
  );

  // Build current tab configuration from Sitecore props with data for dynamic options
  const currentTabConfig = useMemo(() => {
    return getTabConfigurationByIndex(
      tabValue,
      props.fields.Referral,
      props.fields.NonReferral,
      props.fields.ProcessedToCC
    );
  }, [tabValue, props.fields, data]);

  // Build PageConfig from currentTabConfig for table and filters
  const sitecoreConfig = useMemo(() => {
    return buildPageConfig(currentTabConfig);
  }, [currentTabConfig]);

  // Get expanded view configuration
  const expandedConfig = useMemo(() => {
    return getExpandedViewConfig(currentTabConfig, {
      personalInfoHeaderText: props.fields.PersonalInfoHeaderText?.value,
      demographicsHeaderText: props.fields.DemographicsHeaderText?.value,
      caregiverInfoHeaderText: props.fields.CaregiverInfoHeaderText?.value,
      trackingParamsHeaderText: props.fields.TrackingParamsHeaderText?.value,
      dedupAddParamsHeaderText: props.fields.DedupAddParamsHeaderText?.value,
      commentsHeaderText: props.fields.CommentsHeaderText?.value,
    });
  }, [currentTabConfig, props.fields]);

  // Load configuration and data from CustomApp (legacy - keeping for updateTabData and updateRecordInAllTabs)
  const { currentTabData, updateTabData, updateRecordInAllTabs } = useAppData(tabValue);

  // Manage referral data state
  const {
    data: referralData,
    setData: setReferralData,
    lockedRecordsCount,
    handleSelectRow,
    handleSelectAll: handleSelectAllData,
    handlePatientCCIdChange: handlePatientCCIdChangeData,
    handleStatusChange: handleStatusChangeData,
    handleLockToggle,
  } = useReferralData({ currentUser: CURRENT_USER, maxLockedRecords: MAX_LOCKED_RECORDS });

  // Sync Redux data with referralData when tab changes or data loads
  useEffect(() => {
    if (data.length > 0) {
      setReferralData(data);
    } else if (data.length === 0 && referralData.length > 0) {
      setReferralData([]);
    }
  }, [tabValue, data, setReferralData]);

  // Sync referralData changes back to allData (debounced to prevent loops)
  useEffect(() => {
    if (sitecoreConfig && referralData.length > 0) {
      const dataKey = sitecoreConfig.tabs[tabValue]?.dataKey;
      if (dataKey && currentTabData.length > 0) {
        // Only update if there are actual changes to prevent circular updates
        const hasChanges = JSON.stringify(referralData) !== JSON.stringify(currentTabData);
        if (hasChanges) {
          updateTabData(dataKey, () => referralData);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralData]);

  // Manage merge modal
  const {
    mergeModalOpen,
    selectedMergeRow,
    selectedMergeTarget,
    handleOpenMergeModal: openMergeModal,
    handleCloseMergeModal,
    handleMerge: performMerge,
    dialogMode,
    resultMessage,
    resultType,
    showDialog,
    setShowDialog,
    isSending,
  } = useMergeModal();

  // Calculate pagination values from server response
  const totalPages = useMemo(
    () => Math.ceil(totalRecords / pageSize) || 1,
    [totalRecords, pageSize]
  );

  const paginatedData = referralData;

  // Calculate active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    let hasMatchPercentage = false;

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '' && value !== null && (!Array.isArray(value) || value.length > 0)) {
        if (key === 'matchPercentageMin' || key === 'matchPercentageMax') {
          hasMatchPercentage = true;
        } else {
          count++;
        }
      }
    });

    if (hasMatchPercentage) {
      count++;
    }

    return count;
  }, [filters]);

  // Local filter state for UI updates before applying
  const [localFilters, setLocalFilters] = useState<Record<string, string | string[]>>({});

  // Ref to prevent duplicate API calls
  const applyingFiltersRef = useRef(false);

  // Sync local filters with Redux filters when tab changes
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters, tabValue]);

  // Handle filter removal from ActiveFilters - triggers immediate API call
  const handleRemoveFilter = useCallback(
    (field: string, value: string | string[]) => {
      setLocalFilters((prev) => {
        const updatedFilters = { ...prev, [field]: value };
        // Trigger API call with updated filters
        applyFilters(updatedFilters);
        return updatedFilters;
      });
    },
    [applyFilters]
  );

  // Apply filters - triggers API call with complete filter object
  const handleApplyFilters = useCallback(
    (filtersToApply: Record<string, string | string[]>) => {
      if (applyingFiltersRef.current) {
        return;
      }

      applyingFiltersRef.current = true;

      const cleanedFilters = Object.entries(filtersToApply).reduce((acc, [key, value]) => {
        const isEmpty =
          value === null || value === '' || (Array.isArray(value) && value.length === 0);

        if (!isEmpty) {
          acc[key] = value;
        }

        return acc;
      }, {} as Record<string, string | string[]>);

      setLocalFilters(cleanedFilters);
      applyFilters(cleanedFilters);

      setTimeout(() => {
        applyingFiltersRef.current = false;
      }, 1000);
    },
    [applyFilters]
  );

  const handleClearFiltersWithReset = useCallback(() => {
    setLocalFilters({});
    applyFilters({});
  }, [applyFilters]);

  // Selection state
  const allSelected = useMemo(
    () => paginatedData.length > 0 && paginatedData.every((item: ReferralData) => item.selected),
    [paginatedData]
  );
  const someSelected = useMemo(
    () => paginatedData.some((item: ReferralData) => item.selected),
    [paginatedData]
  );

  // Event handlers
  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      setTabValue(newValue);
      setFilterOpen(false); // Close filter panel when tab changes
      setLocalFilters({});
      applyFilters({});
    },
    [applyFilters]
  );

  const handleSelectAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      const visibleIds = paginatedData.map((item: ReferralData) => item.id);
      handleSelectAllData(checked, visibleIds);
    },
    [paginatedData, handleSelectAllData]
  );

  const handlePageChange = useCallback(
    (_event: React.ChangeEvent<unknown>, value: number) => {
      changePage(value);
    },
    [changePage]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      changePageSize(newPageSize);
    },
    [changePageSize]
  );

  // Handle sorting changes from table column headers
  const handleSort = useCallback(
    (field: string) => {
      let newSortOrder: 'asc' | 'desc' = 'asc';

      if (sortBy === field) {
        newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      }

      changeSorting(field, newSortOrder);
    },
    [sortBy, sortOrder, changeSorting]
  );

  const handlePatientCCIdChange = useCallback(
    (id: string, newValue: string) => {
      handlePatientCCIdChangeData(id, newValue);
      updateRecordInAllTabs(id, (record) => ({ ...record, patientCCId: newValue }));
    },
    [handlePatientCCIdChangeData, updateRecordInAllTabs]
  );

  const handleStatusChange = useCallback(
    (id: string, newValue: string) => {
      handleStatusChangeData(id, newValue);
      updateRecordInAllTabs(id, (record) => ({ ...record, status: newValue }));
    },
    [handleStatusChangeData, updateRecordInAllTabs]
  );

  const handleOpenMergeModal = useCallback(
    (match: DeduplicationMatch | undefined, row: ReferralData) => {
      openMergeModal(match, row);
    },
    [openMergeModal]
  );

  const handleRemoveRecord = useCallback(
    (patientId: string) => {
      setReferralData((prevData) => prevData.filter((item) => item.patientId !== patientId));

      updateTabData(currentTabKey, (currentData) =>
        currentData.filter((item) => item.patientId !== patientId)
      );

      if (currentTabKey === TAB_KEYS.REFERRALS) {
        dispatch(deleteReferralRecordByPatientId(patientId));
      }
    },
    [setReferralData, currentTabKey, updateTabData, dispatch, referralData]
  );

  const handleCancelAction = () => {
    setShowDialog(false);
  };

  const handleCloseDialog = () => {
    console.log({ selectedMergeRow });
    if (dialogMode === 'result' && resultType === 'success' && selectedMergeRow?.patientId) {
      handleRemoveRecord(selectedMergeRow.patientId);
      // Close the merge modal after successful merge
      handleCloseMergeModal();
    }
    setShowDialog(false);
  };

  const isProcessedTab = currentTabKey === TAB_KEYS.PROCESSED_REFERRALS;
  const hideTabSpecificFeatures = currentTabKey === TAB_KEYS.PROCESSED_REFERRALS;

  if (!sitecoreConfig) {
    return (
      <StyledContainer maxWidth="xl">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </StyledContainer>
    );
  }

  const headerFields = {
    HeaderTitle: props.fields.HeaderTitle,
    HeaderSummary: props.fields.HeaderSummary,
    LogoutLink: props.fields.LogoutLink,
  };

  return (
    <ThemeProvider theme={customTheme}>
      <Header fields={headerFields} />
      <StyledContainer maxWidth={false}>
        <StyledPaper elevation={0}>
          <TabBar
            tabs={dynamicTabs}
            activeTab={tabValue}
            onTabChange={handleTabChange}
            allTabTotals={allTabTotals}
            guidanceButtonText={props.fields.GuidanceButtonText}
            guidanceTitle={props.fields.GuidanceTitle}
            guidanceBody={props.fields.GuidanceBody}
          />

          <AlertMessage messageText={props.fields.AlertMessageText} />

          {error && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1.5,
                    flexWrap: 'wrap',
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flex: 1 }}>
                    {sitecoreConfig.enableFilters && (
                      <Box sx={{ position: 'relative' }}>
                        <FilterButton
                          filterOpen={filterOpen}
                          onToggleFilter={() => setFilterOpen(!filterOpen)}
                          activeFilterCount={activeFilterCount}
                          label={props.fields.FilterHeading}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>

                <DynamicFilterPanel
                  filterOpen={filterOpen}
                  filters={localFilters}
                  onClearFilters={handleClearFiltersWithReset}
                  filterConfig={sitecoreConfig.filterConfig}
                  currentTabIndex={tabValue}
                  currentTabKey={currentTabKey}
                  onApplyFilters={handleApplyFilters}
                />

                <ActiveFilters
                  filters={filters}
                  onFilterChange={handleRemoveFilter}
                  totalRecords={totalRecords}
                  filteredRecords={totalRecords}
                  filterConfig={sitecoreConfig.filterConfig}
                />

                <ActiveSorts
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onClearSort={clearSorting}
                  columns={sitecoreConfig.columns}
                />

                <CustomTable
                  config={sitecoreConfig}
                  someSelected={someSelected}
                  allSelected={allSelected}
                  handleSelectAll={handleSelectAll}
                  paginatedData={paginatedData}
                  handleSelectRow={handleSelectRow}
                  onPatientCCIdChange={handlePatientCCIdChange}
                  onStatusChange={handleStatusChange}
                  sortConfigs={[]}
                  onSort={handleSort}
                  onLockToggle={handleLockToggle}
                  currentUser={CURRENT_USER}
                  lockedRecordsCount={lockedRecordsCount}
                  onOpenMergeModal={handleOpenMergeModal}
                  hideLockColumn={hideTabSpecificFeatures}
                  hideDedupeSelectColumn={hideTabSpecificFeatures}
                  hideSendToCCButton={hideTabSpecificFeatures}
                  isProcessedTab={isProcessedTab}
                  expandedConfig={expandedConfig}
                  loading={loading}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onRemoveRecord={handleRemoveRecord}
                  confirmationModalMessages={props.fields}
                />

                <PaginationControls
                  totalPages={totalPages}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  totalItems={totalRecords}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Merge Comparison Modal */}
          {selectedMergeRow && selectedMergeTarget && mergeModalOpen && (
            <MergeComparisonModal
              open={mergeModalOpen}
              onClose={handleCloseMergeModal}
              sourceRecord={selectedMergeRow}
              targetRecord={selectedMergeTarget}
              onMerge={performMerge}
              confirmationModalMessages={props.fields}
              isSending={isSending}
            />
          )}
          <ConfirmationDialog
            open={showDialog}
            onClose={handleCloseDialog}
            onConfirm={handleCloseDialog}
            onCancel={handleCancelAction}
            mode={dialogMode}
            message={
              dialogMode === 'confirm'
                ? props?.fields?.ConfirmText?.value ||
                  'Are you sure you want to send this record to CC?'
                : resultMessage
            }
            title="Confirm Send to CC"
            confirmButtonText={props?.fields?.ConfirmButtonText?.value || 'Yes'}
            cancelButtonText={props?.fields?.ConfirmDismissButtonText?.value || 'Cancel'}
          />
        </StyledPaper>
      </StyledContainer>
      <ToastNotification />
    </ThemeProvider>
  );
};

export default LeadManagement;
